/* eslint-disable @typescript-eslint/no-explicit-any */
import * as d3 from 'd3';
import type { Selection } from 'd3';
import type { RefObject } from 'react';
import type {
  ChartIconType,
  ElementSizeType,
  HeadType,
  ReportPageScatterChartQueueType,
  ScatterClassIconType,
  ScatterStudentIconType,
} from '../types';
import {
  CHARTS_ANIMATION_DURATION,
  HIGHLIGHTED_STUDENT_ICON_HEIGHT,
  HIGHLIGHTED_STUDENT_ICON_WIDTH,
  PROGRESS_LABEL_FONTSIZE,
  PROGRESS_LABEL_HEIGHT,
  PROGRESS_LABEL_HIGHLIGHT_STROKE_WIDTH,
  PROGRESS_LABEL_MARKER_RADIUS,
  PROGRESS_LABEL_MAX_WIDTH,
  PROGRESS_LABEL_ROUNDED,
  PROGRESS_LABEL_STROKE_WIDTH,
  STUDENT_ICON_HEIGHT,
  STUDENT_ICON_WIDTH,
  STUDENT_PROGRESS_CLASSES,
  STUDENT_PROGRESS_MAX_HEIGHT,
  TOOLTIP_WIDTH,
  TOPIC_PROGRESS_AVERAGE_DASHARRAY,
  TOPIC_PROGRESS_AVERAGE_WIDTH,
  YEAR_GROUP_ICON_HEIGHT,
  YEAR_GROUP_ICON_HIGHLIGHTED_HEIGHT,
  YEAR_GROUP_ICON_HIGHLIGHTED_WIDTH,
  YEAR_GROUP_ICON_WIDTH,
} from './vars';
import * as StudentIcon from '../icons/students';
import * as ClassIcon from '../icons/classes';
import type { TFunction } from 'i18next';
import { readableColor, tint } from 'polished';
import { APP_THEME } from '../../../styles/theme/styled/theme';
import { randomNumber } from '../../../util/numbers';

export const EPSILON = 1e-3;

/**
 * Checks if device has touching screen.
 */
const isTouchDevice = (): boolean => {
  return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

/**
 * Prepares parent container.
 * @param ref
 */
const prepareContainer = (ref: RefObject<HTMLDivElement>): Selection<HTMLDivElement | null, unknown, null, undefined> => {
  const container = d3.select(ref.current);
  container
    .style('position', 'relative')
    .selectAll('*')
    .remove();
  return container;
};

/**
 * Prints linearGradient.
 * @param linearGradient
 * @param id
 * @param orientation
 * @param from
 * @param to
 */
const prepareGradient = (
  linearGradient: Selection<SVGLinearGradientElement, undefined, null, undefined>,
  id: string,
  orientation: 'vertical' | 'horizontal',
  from: string,
  to: string,
): void => {

  linearGradient
    .attr('id', id);

  linearGradient
    .append('stop')
    .attr('offset', '0%')
    .attr('stop-color', from);

  linearGradient
    .append('stop')
    .attr('offset', '100%')
    .attr('stop-color', to);

  if (orientation === 'horizontal') {
    linearGradient
      .attr('x1', '0%')
      .attr('x2', '100%');
  }

  if (orientation === 'vertical') {
    linearGradient
      .attr('y1', '0%')
      .attr('y2', '100%');
  }

};

/**
 * Prepares settings for average lines.
 * @param line
 * @param color
 * @param height
 * @param from
 * @param to
 * @param width
 */
const prepareAverageLine = (
  line: Selection<SVGLineElement, undefined, null, undefined>,
  color: string,
  height: number,
  from: number,
  to: number,
  width = TOPIC_PROGRESS_AVERAGE_WIDTH,
): void => {
  line
    .attr('x1', from)
    .attr('x2', from)
    .attr('y1', 0)
    .attr('y2', height)
    .attr('stroke-width', width)
    .attr('stroke-dasharray', TOPIC_PROGRESS_AVERAGE_DASHARRAY)
    .style('stroke', color)
    .call((line) => {
      line
        .transition()
        .duration(CHARTS_ANIMATION_DURATION)
        .ease(d3.easeBackOut)
        .attr('x1', to)
        .attr('x2', to);
    })
  ;
};

/**
 * Prints custom path as a bar arrow.
 * @param y
 * @param height
 * @param start
 * @param end
 */
const barArrow = (height: number, start: number, end: number, y = 0): string => {
  const offset_y = height / 4;
  const flip = end < start;
  const arrowTip = flip ? end + offset_y : end - offset_y;

  return `M ${start} ${y} L ${arrowTip} ${y} L ${end} ${y + (height / 2)} L ${arrowTip} ${y + height} L ${start} ${y + height} Z`;
};

/**
 * Prints custom path as a arrow representing filtered data.
 * @param start
 * @param end
 * @param height
 * @param y
 * @deprecated
 */
const filteredArrow = (start: number, end: number, height: number, y = 0): string => {
  const center = y + (height / 2);
  const bottom = y + height;
  const arrowLength = 10;

  const flip = end < start;
  const arrowHead = flip ? end + arrowLength : end - arrowLength;
  const endWithOffset = flip ? end - 1.35 : end + 1.35;

  return `M ${start} ${y} L ${start} ${bottom} M ${start} ${center} L ${end} ${center} L ${arrowHead} ${center - arrowLength} M ${endWithOffset} ${center} L ${arrowHead} ${center + arrowLength} Z`;
};

/**
 * Prints label element for scatter chart.
 * @param text
 * @param element
 * @param color
 * @param fill
 */
const scatterLabel = (showOthersData: boolean, highlighted: boolean, text: string, element: Selection<SVGGElement, unknown, null, undefined>, color: string, fill = false): void => {

  let label = text;
  let readable;
  const labelWidth = getTextWidth(label, PROGRESS_LABEL_FONTSIZE);

  if (labelWidth > PROGRESS_LABEL_MAX_WIDTH) {
    label = sliceText(label);
  }

  const reduceVisibility = showOthersData && !highlighted;
  const colorStyle = reduceVisibility ? tint(0.75, color) : color;
  const labelGroup = element.append('g');

  if (highlighted) {
    labelGroup.append('rect')
      .attr('width', PROGRESS_LABEL_MAX_WIDTH)
      .attr('height', PROGRESS_LABEL_HEIGHT)
      .attr('rx', PROGRESS_LABEL_ROUNDED)
      .attr('stroke-width', PROGRESS_LABEL_HIGHLIGHT_STROKE_WIDTH)
      .style('fill', APP_THEME.colors.primary)
      .style('stroke', APP_THEME.colors.primary);
  }
  const rect = labelGroup.append('rect');
  rect
    .attr('width', PROGRESS_LABEL_MAX_WIDTH)
    .attr('height', PROGRESS_LABEL_HEIGHT)
    .attr('rx', PROGRESS_LABEL_ROUNDED);

  if (fill) {
    readable = readableColor(colorStyle);
    rect
      .style('fill', colorStyle)
      .style('stroke', 'white')
      .attr('stroke-width', highlighted ? 1 : PROGRESS_LABEL_STROKE_WIDTH);
  } else {
    readable = readableColor('white');
    rect
      .style('fill', 'white')
      .style('stroke', colorStyle)
      .attr('stroke-width', PROGRESS_LABEL_STROKE_WIDTH);
  }

  const group = labelGroup
    .append('svg')
    .attr('width', PROGRESS_LABEL_MAX_WIDTH)
    .attr('height', PROGRESS_LABEL_HEIGHT);

  group
    .append('text')
    .attr('x', '50%')
    .attr('y', '50%')
    .attr('alignment-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .style('font-size', PROGRESS_LABEL_FONTSIZE)
    .style('fill', readable)
    .text(label);

  const marker = labelGroup.append('circle');
  marker
    .classed('marker', true)
    .attr('r', PROGRESS_LABEL_MARKER_RADIUS)
    .style('fill', colorStyle)
    .style('stroke', highlighted ? APP_THEME.colors.primary : 'white')
    .attr('stroke-width', highlighted ?  PROGRESS_LABEL_STROKE_WIDTH : 1)
    .attr('cx', PROGRESS_LABEL_MAX_WIDTH * .5);
};

/**
 * Computes a string width in the DOM.
 * @param text
 * @param fontSize
 */
const getTextWidth = (text: string, fontSize: string): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.font = `${fontSize} bold`;
  return context.measureText(text).width;
};

/**
 * Slices text content.
 * @param text
 * @returns
 */
const sliceText = (text: string): string => {
  return text.slice(0, text.length / 2.5) + '...';
};

/**
 * Returns chart element width/height based on vars.
 * @param type
 */
const getElementSize = (type: ChartIconType): ElementSizeType => {
  switch (type) {
    case 'student':
      return {
        element_width: STUDENT_ICON_WIDTH,
        element_height: STUDENT_ICON_HEIGHT,
        highlighted_width: HIGHLIGHTED_STUDENT_ICON_WIDTH,
        highlighted_height: HIGHLIGHTED_STUDENT_ICON_HEIGHT,
      };
    case 'class':
    case 'year':
      return {
        element_width: YEAR_GROUP_ICON_WIDTH,
        element_height: YEAR_GROUP_ICON_HEIGHT,
        highlighted_width: YEAR_GROUP_ICON_HIGHLIGHTED_WIDTH,
        highlighted_height: YEAR_GROUP_ICON_HIGHLIGHTED_HEIGHT,
      };
    default:
      return {
        element_width: PROGRESS_LABEL_MAX_WIDTH,
        element_height: PROGRESS_LABEL_HEIGHT,
        highlighted_width: PROGRESS_LABEL_MAX_WIDTH,
        highlighted_height: PROGRESS_LABEL_HEIGHT,
      };
  }
};

/**
 * Returns double radius.
 * @param radius
 * @deprecated
 */
const getRadius2 = (radius: number): number => {
  return radius ** 2;
};

/**
 * Given an array of x-values and a separation radius,
 * returns an array of y-values.
 * @param X
 * @param radius
 * @deprecated
 */
const dodge = (X: number[], radius: number): Array<Float64Array> => {
  const Y = new Float64Array(X.length);
  const transformedX = new Float64Array(X.length);
  radius = radius / 2;
  const radius2 = getRadius2(radius);

  let head: unknown = null;
  let tail: unknown = null;

  //grouping states
  let maxHeightLevel = 2;
  let currentLevel = 2;
  let currentElementsInLevel:Array<number> = [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 ];
  let direction = 1;
  const zIndex = new Float64Array(X.length);

  // Place each circle sequentially.
  for (const bi of d3.range(X.length).sort((i, j) => X[i] - X[j])) {
    // Remove circles from the queue that can't intersect the new circle b.
    while (head && X[(head as HeadType).index as number] < X[bi] - radius2) {
      head = (head as HeadType).next as number;
    }
    // Choose the min non-intersecting tangent.
    if (intersects(X, Y, X[bi], (Y[bi] = 0), radius, head)) {
      let a = head;
      Y[bi] = Infinity;
      do {
        const ai = (a as HeadType).index as number;
        const distance = Math.sqrt(radius2 - (X[ai] - X[bi]) ** 2);
        const y = Y[ai] + (distance + distance * (currentLevel - 1));
        if (bi >= dodgeRecursion(maxHeightLevel)) {
          maxHeightLevel = maxHeightLevel + 1;
        }

        if (y < Y[bi] && !intersects(X, Y, X[bi], y, radius, head)) {
          //is level filled
          if (currentElementsInLevel[currentLevel] === dodgeRecursion(currentLevel)) {
            currentLevel = ((currentLevel + 1) % maxHeightLevel) + 1;
            direction = 1;
          }
          currentElementsInLevel[currentLevel] = currentElementsInLevel[currentLevel] + 1;
          Y[bi] = y;
          zIndex[bi] = currentLevel;
        }
        a = (a as HeadType).next;
      } while (a);
      transformedX[bi] = X[bi]  + radius / 2 * ((currentElementsInLevel[currentLevel] - (direction < 0 ? 1 : 0)) * direction);
      direction *= -1;
    } else {
      //restore grouping states
      direction = 1;
      transformedX[bi] = X[bi];
      maxHeightLevel = 2;
      currentLevel = 2;
      currentElementsInLevel = [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 ];
    }
    // Add b to the queue.
    const b: HeadType = {
      index: bi,
      next: null,
    };

    if (head === null) {
      head = tail = b;
    } else {
      tail = b;
      (tail as any).next = b;
    }
  }

  return [ transformedX, Y, zIndex ];
};

/**
 *
 * @param n
 * @param memo
 * @deprecated
 */
function dodgeRecursion(n: number, memo: any = {}) {
  if (n < 2) {
    return n;
  }

  if (!memo[n]) {
    memo[n] = n + 2;
  }
  return memo[n];
}

/**
 * Returns true if element {x,y} intersects with any other element in the queue.
 * @param X
 * @param Y
 * @param x
 * @param y
 * @param radius
 * @param head
 * @deprecated
 */
const intersects = (
  X: number[],
  Y: Float64Array,
  x: number,
  y: number,
  radius: number,
  head: unknown,
): boolean => {
  let a: unknown = head;
  while (a) {
    const ai = (a as HeadType).index as number;
    if (getRadius2(radius) - EPSILON > (X[ai] - x) ** 2 + (Y[ai] - y) ** 2) {
      return true;
    }
    a = (a as HeadType).next as number;
  }

  return false;
};

/**
 * Returns x-y positions for scatter elements.
 * @param elements
 * @param sizes
 */
const queue = (elements: Array<ReportPageScatterChartQueueType>, sizes: ElementSizeType, isLabel: boolean): Array<ReportPageScatterChartQueueType> => {

  const { element_width, element_height } = sizes;
  const max_height = STUDENT_PROGRESS_MAX_HEIGHT;

  const x_spread = element_width / 2; // spreading in the x direction
  const pos = [ -1, 1 ];
  const max_y = 30;
  const spaceHeight = element_height * 1.1;

  let previousBottomElement: ReportPageScatterChartQueueType;
  let spaceCount = 1;
  let min_y = 1;
  elements.map((element, index) => {
    const previousElement = index > 0 ? elements[index - 1] : null;
    let x = element.x - x_spread;
    let y = element.y ?? 0;
    if (isLabel) {
      if (previousElement !== null) {
        if (x < previousBottomElement.x + element_width) {
          y = (previousElement.y ?? 0) + (spaceHeight * spaceCount);
          spaceCount *= 0.8;
        }
      }
      if (y === 0 || index == 0) {
        previousBottomElement = element;
        spaceCount = 1;
      }
    } else {
      if (previousElement?.value === element.value) {
        x += randomNumber(-10, 10) + pos[Math.round(randomNumber(0, 1))];
        y += randomNumber(min_y, Math.min(min_y + 4, max_y));
        min_y += 5;

        if (min_y >= max_height) {
          min_y = 1;
        }
      } else {
        min_y = 1;
      }
    }
    element.x = x;
    element.y = y;
  });

  return elements;
};

/**
 * Checks if axis should break in case the gap between first and next value is too big.
 */
const axisShouldBreak = (data: number[]): (boolean | number)[] => {
  const uniqueValues = Array
    .from(d3.intersection(data))
    .sort(d3.ascending);

  const spaces: number[] = [];

  for (let i = 1; i < uniqueValues.length; i++) {
    spaces.push(uniqueValues[i] - uniqueValues[i - 1]);
  }

  const mean = d3.mean(spaces) as number;

  const first = uniqueValues[0];
  const next = uniqueValues[1];

  const breaks = first <= 0 && (next - first) > mean;

  return [ breaks, first, next ];
};

/**
 * Prepares xml file for be appended to node.
 * @param xml
 * @returns
 */
const prepareXml = (xml: XMLDocument): Node => {
  return xml.importNode(xml.documentElement, true);
};

const getSvg = async(svgUrl: string): Promise<Node> => prepareXml(await d3.xml(svgUrl));

/**
 * Returns an array of svg icons representing students.
 */
const getStudentIcons = async(): Promise<ScatterStudentIconType> => ({
  primary: {
    close: await getSvg(StudentIcon.SelectedClose),
    open: await getSvg(StudentIcon.SelectedOpen),
    bounce: await getSvg(StudentIcon.SelectedHeadBounce),
    jump: await getSvg(StudentIcon.SelectedJumpSpin),
    snooze: await getSvg(StudentIcon.SelectedSnooze),
    skipping_rope: await getSvg(StudentIcon.SelectedSkippingRope),
    glasses: await getSvg(StudentIcon.SelectedGlasses),
    juggler: await getSvg(StudentIcon.SelectedJuggler),
  },
  male: {
    close: await getSvg(StudentIcon.MaleClose),
    open: await getSvg(StudentIcon.MaleOpen),
    bounce: await getSvg(StudentIcon.MaleHeadBounce),
    jump: await getSvg(StudentIcon.MaleJumpSpin),
    snooze: await getSvg(StudentIcon.MaleSnooze),
    skipping_rope: await getSvg(StudentIcon.MaleSkippingRope),
    glasses: await getSvg(StudentIcon.MaleGlasses),
    juggler: await getSvg(StudentIcon.MaleJuggler),
  },
  female: {
    close: await getSvg(StudentIcon.FemaleClose),
    open: await getSvg(StudentIcon.FemaleOpen),
    bounce: await getSvg(StudentIcon.FemaleHeadBounce),
    jump: await getSvg(StudentIcon.FemaleJumpSpin),
    snooze: await getSvg(StudentIcon.FemaleSnooze),
    skipping_rope: await getSvg(StudentIcon.FemaleSkippingRope),
    glasses: await getSvg(StudentIcon.FemaleGlasses),
    juggler: await getSvg(StudentIcon.FemaleJuggler),
  },
  other: {
    close: await getSvg(StudentIcon.OtherClose),
    open: await getSvg(StudentIcon.OtherOpen),
    bounce: await getSvg(StudentIcon.OtherHeadBounce),
    jump: await getSvg(StudentIcon.OtherJumpSpin),
    snooze: await getSvg(StudentIcon.OtherSnooze),
    skipping_rope: await getSvg(StudentIcon.OtherSkippingRope),
    glasses: await getSvg(StudentIcon.OtherGlasses),
    juggler: await getSvg(StudentIcon.OtherJuggler),
  },
});

/**
 * Returns an array of svg icons representing classes.
 */
const getClassIcons = async(): Promise<ScatterClassIconType> => ({
  selected: await getSvg(ClassIcon.SelectedClass),
  positive: await getSvg(ClassIcon.PositiveClass),
  negative: await getSvg(ClassIcon.NegativeClass),
});

/**
 * Prepares tooltip container. Appends it to container.
 */
const prepareTooltip = (container: Selection<HTMLDivElement | null, unknown, null, undefined>): Selection<HTMLDivElement, undefined, null, undefined> => {
  const tooltip = d3.create('div');
  tooltip
    .attr('class', 'absolute pointer-events-none whitespace-pre-line rounded-default border text-center bg-white shadow p-sm')
    .style('max-width', TOOLTIP_WIDTH)
    .style('top', 0)
    .style('left', 0)
    .style('opacity', 0);

  container.node()?.appendChild(tooltip.node() as Node);

  return tooltip;
};

/**
 * Toggles tooltip's div container.
 * @param tooltip
 * @param show
 */
const toggleTooltip = (tooltip: Selection<HTMLDivElement, undefined, null, undefined>, show = true): void => {
  tooltip
    .transition()
    .duration(200)
    .style('opacity', show ? 1 : 0);
};

/**
 * Moves tooltip to x-y positions.
 * @param tooltip
 * @param width
 * @param x
 * @param y
 */
const moveTooltip = (tooltip: Selection<HTMLDivElement, undefined, null, undefined>, width: number, x: number, y: number): void => {
  let offset = 0;

  if (x + TOOLTIP_WIDTH / 2 >= width) {
    offset = TOOLTIP_WIDTH / 2;
  } else if (x - TOOLTIP_WIDTH <= 0) {
    offset = -TOOLTIP_WIDTH / 2;
  }

  tooltip
    .style('transform', `translate(${(x - TOOLTIP_WIDTH / 2) - offset}px, ${y - TOOLTIP_WIDTH / 3}px)`);
};

/**
 * Prints tooltip message with 'Student name - chart value' format.
 * @param tooltip
 * @param name
 * @param value
 */
const printTooltipText = (tooltip: Selection<HTMLDivElement, undefined, null, undefined>, name: string, value: string): void => {
  tooltip
    .text(`${name}: ${value}`);
};

/**
 * Prints tooltip message with Topic Progress format.
 * @param tooltip
 * @param initialStudents
 * @param ageAtStart
 * @param ageAtEnd
 * @param iconType
 * @param translate
 * @param isHighlighted
 * */
const topicProgressTooltip = (tooltip: Selection<HTMLDivElement, undefined, null, undefined>,
  initialStudents: number,
  ageAtStart: number,
  ageAtEnd: number,
  iconType: ChartIconType,
  translate: TFunction,
  showSensitiveData: boolean,
  isHighlighted?: boolean,
): void => {
  tooltip.html(
    `<table class="text-left text-sm">
      <tr>
        <td><strong>${translate(`pages.REPORTS.SECTIONS.TOPIC_PROGRESS.TOOLTIP.${iconType}`)}</strong></td>
        <td class="text-right pl-md"> ${initialStudents}</td>
      </tr>
      ${showSensitiveData ? `
      <tr>
        <td><strong>${translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS.TOOLTIP.start')}</strong></td>
        <td class="text-right pl-md"> ${ageAtStart.toFixed(2)}</td>
      </tr>
      <tr>
        <td><strong>${translate('pages.REPORTS.SECTIONS.TOPIC_PROGRESS.TOOLTIP.end')}</strong></td>
        <td class="text-right pl-md"> ${ageAtEnd.toFixed(2)}</td>
      </tr>` : ''}
      <tr>
        <td><strong>${translate(`pages.REPORTS.SECTIONS.TOPIC_PROGRESS.TOOLTIP.${isHighlighted ? '' : iconType + '_'}improvement`)}</strong></td>
        <td class="text-right pl-md"> ${(ageAtEnd - ageAtStart).toFixed(2)}</td>
      </tr>
    </table>`,
  );
};

/**
 * Prevents propagation of the touch start event.
 * @param event
 */
const handleTouchTooltipStart = (event: TouchEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Handles tooltip movement on touching screens.
 * @param event
 * @param tooltip
 * @param width
 */
const handleTouchTooltipMove = (event: TouchEvent, tooltip: Selection<HTMLDivElement, undefined, null, undefined>, width: number): void => {
  const touches = d3.pointers(event);
  const [ x, y ] = touches[0];

  const target = document.elementFromPoint(event.changedTouches[0].clientX, event.changedTouches[0].clientY);

  if (target?.parentElement?.parentElement) {
    const parent = target.parentElement.parentElement;
    const studentName = parent?.getAttribute(STUDENT_PROGRESS_CLASSES.icon);
    const studentValue = parent?.getAttribute(STUDENT_PROGRESS_CLASSES.value);

    if (studentName && studentValue) {
      printTooltipText(tooltip, studentName, studentValue);
      moveTooltip(tooltip, width, x, y);
      toggleTooltip(tooltip);
    }
  } else {
    toggleTooltip(tooltip, false);
  }
};

export default {
  isTouchDevice,
  prepareContainer,
  prepareGradient,
  prepareAverageLine,
  getTextWidth,
  sliceText,
  getElementSize,
  barArrow,
  filteredArrow,
  scatterLabel,
  getRadius2,
  dodge,
  intersects,
  queue,
  axisShouldBreak,
  prepareXml,
  getStudentIcons,
  getClassIcons,
  prepareTooltip,
  toggleTooltip,
  moveTooltip,
  printTooltipText,
  topicProgressTooltip,
  handleTouchTooltipStart,
  handleTouchTooltipMove,
};
